// ===========================================
// Location → Room Picker
// ===========================================
// Two-step picker for the Change Location dialog: choose a studio (Location),
// then a room within it. Shows room capacity and a soft warning when the chosen
// room can't hold the enrolled students. Reuses useLocations() (which returns
// each location with its nested rooms), so no extra room fetch is needed.

import { useMemo, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { useLocations } from '../../../hooks/useAdmin';

interface LocationRoomPickerProps {
  /** Currently selected target room id (controlled). */
  value: string;
  onChange: (roomId: string) => void;
  /** The lesson's CURRENT room id — its tile is labelled "(current)". */
  currentRoomId?: string;
  /** Enrolled student count — drives the soft capacity warning. */
  enrolledCount?: number;
  disabled?: boolean;
}

export default function LocationRoomPicker({
  value,
  onChange,
  currentRoomId,
  enrolledCount,
  disabled,
}: LocationRoomPickerProps) {
  const { data: locations = [], isLoading } = useLocations();

  // Active locations that have at least one active room. NOTE: the locations
  // API nests rooms under the PascalCase Prisma relation `Room` (already
  // filtered to active server-side); we read `Room` first and fall back to the
  // legacy `rooms` key for safety, then normalise to a local `rooms` array.
  const activeLocations = useMemo(
    () =>
      locations
        .filter((l) => l.isActive !== false)
        .map((l) => ({
          ...l,
          rooms: (l.Room ?? l.rooms ?? []).filter((r) => r.isActive !== false),
        }))
        .filter((l) => l.rooms.length > 0),
    [locations]
  );

  // The location of the currently-selected room (derived from value).
  const selectedLocationId = useMemo(() => {
    for (const loc of activeLocations) {
      if (loc.rooms.some((r) => r.id === value)) return loc.id;
    }
    return '';
  }, [activeLocations, value]);

  // Default the location selector to the current room's location on first load.
  useEffect(() => {
    if (!value && currentRoomId) {
      const isStillValid = activeLocations.some((l) => l.rooms.some((r) => r.id === currentRoomId));
      if (isStillValid) onChange(currentRoomId);
    }
  }, [value, currentRoomId, activeLocations, onChange]);

  const roomsForLocation = useMemo(
    () => activeLocations.find((l) => l.id === selectedLocationId)?.rooms ?? [],
    [activeLocations, selectedLocationId]
  );

  const selectedRoom = useMemo(
    () => roomsForLocation.find((r) => r.id === value),
    [roomsForLocation, value]
  );

  const overCapacity =
    selectedRoom != null &&
    enrolledCount != null &&
    selectedRoom.capacity < enrolledCount;

  const handleLocationChange = (locationId: string) => {
    // Switching studio clears the room selection (force a fresh room pick).
    const loc = activeLocations.find((l) => l.id === locationId);
    const firstRoom = loc?.rooms[0];
    onChange(firstRoom ? firstRoom.id : '');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (activeLocations.length === 0) {
    return <Alert severity="info">No active rooms are configured for this school.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel id="change-location-studio-label">Studio</InputLabel>
        <Select
          labelId="change-location-studio-label"
          label="Studio"
          value={selectedLocationId}
          onChange={(e) => handleLocationChange(e.target.value)}
        >
          {activeLocations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth disabled={disabled || !selectedLocationId}>
        <InputLabel id="change-location-room-label">Room</InputLabel>
        <Select
          labelId="change-location-room-label"
          label="Room"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {roomsForLocation.map((room) => (
            <MenuItem key={room.id} value={room.id}>
              {room.name} · cap {room.capacity}
              {room.id === currentRoomId ? ' (current)' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {overCapacity && (
        <Alert severity="warning">
          {selectedRoom!.name} holds {selectedRoom!.capacity}, but {enrolledCount} students are
          enrolled. You can still move the class, but it will be over capacity.
        </Alert>
      )}
    </Box>
  );
}
